import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const I18nContext = createContext(null);

export const translations = {
  en: {
    appName: "Abdullah | Coffee Science",
    tagline: "From seed to sip",
    nav: {
      home: "Home",
      journey: "Journey",
      academy: "Academy",
      encyclopedia: "Encyclopedia",
      countries: "Origins",
      calculator: "Ratio",
      recipes: "Recipes",
      addRecipe: "Add Recipe",
      assistant: "AI Barista",
      about: "About",
      blog: "Journal",
      login: "Sign In",
      logout: "Sign Out",
      mine: "My Recipes",
    },
    home: {
      heroTitle: "Complete Coffee Science.",
      heroLead: "Trace every bean from the highland farm to the cup in your hand. Recipes, ratios, and an AI barista — built for people who actually care.",
      ctaExplore: "Begin the journey",
      ctaRecipes: "Browse recipes",
      sectionJourney: "The journey",
      sectionJourneyLead: "Five stages turn a cherry into a craft.",
      sectionOrigins: "Eight origins",
      sectionOriginsLead: "Each country writes its own flavour into the cup.",
      sectionTools: "Tools for brewers",
      sectionToolsLead: "Calculate, brew, share.",
      missionTag: "Our mission",
      missionTitle: "The Arab world's first reference & community for coffee science.",
      missionLead: "An educational platform built to raise awareness, empower baristas, and equip every coffee lover with trustworthy, standards-based knowledge — so the craft we love grows the way it deserves.",
      missionPillar1Title: "Reference",
      missionPillar1Body: "A bilingual encyclopedia rooted in SCA standards — from cultivation to extraction.",
      missionPillar2Title: "Awareness",
      missionPillar2Body: "Clear, accessible learning paths that turn enthusiasts into informed brewers.",
      missionPillar3Title: "Community",
      missionPillar3Body: "A space where baristas, café owners, and home brewers share recipes and grow together.",
    },
    journey: {
      title: "The journey of coffee",
      lead: "From a tiny seed in volcanic soil to the cup you hold — five stages shape every flavour.",
      stages: [
        {
          tag: "01 · Cultivation",
          title: "Cultivation",
          body: "Arabica thrives between 1,000 and 2,200 metres, in tropical belts with stable temperatures, rich volcanic soil, and shade. A single tree takes 3 to 4 years to bear its first cherries."
        },
        {
          tag: "02 · Harvest",
          title: "Harvest",
          body: "Cherries ripen from green to deep red over months. Selective hand-picking ensures only ripe cherries are gathered — the key to quality specialty coffee."
        },
        {
          tag: "03 · Processing",
          title: "Processing",
          body: "Natural, washed, honey, or anaerobic — how the cherry is processed defines the cup's clarity, sweetness, and complexity. Each method tells a story."
        },
        {
          tag: "04 · Roasting",
          title: "Roasting",
          body: "Heat transforms green beans through Maillard reactions and caramelisation. From light to dark, the roaster decides which flavours to bring forward and which to soften."
        },
        {
          tag: "05 · Brewing",
          title: "Brewing",
          body: "Grind, ratio, water, time, and turbulence. Five variables that, when balanced, extract the soul of the bean into your cup."
        },
      ],
    },
    countries: {
      title: "Coffee origins",
      lead: "Legendary growing regions across the coffee belt — each with its own terroir and tradition.",
      varieties: "Varieties",
      altitude: "Altitude",
      harvest: "Harvest window",
      process: "Processing",
      flavor: "Flavour profile",
    },
    calculator: {
      title: "Ratio calculator",
      lead: "Pick a brew method and we'll dial in your perfect cup.",
      method: "Brew method",
      coffee: "Coffee (g)",
      water: "Water (g)",
      ratio: "Ratio (1:n)",
      calculate: "Calculate",
      result: "Your brew",
      grind: "Grind",
      temp: "Temperature",
      time: "Brew time",
      steps: "Steps",
      defaultRatio: "Default ratio",
    },
    recipes: {
      title: "Recipes",
      lead: "Curated classics plus the best from our community.",
      curated: "Curated",
      community: "Community",
      empty: "No community recipes yet — be the first to share yours.",
      add: "Add recipe",
      method: "Method",
      ingredients: "Ingredients",
      steps: "Steps",
      difficulty: "Difficulty",
      by: "by",
      delete: "Delete",
    },
    addRecipe: {
      title: "Share your recipe",
      lead: "Help the community brew better.",
      titleEn: "Title (English)",
      titleAr: "Title (Arabic)",
      method: "Brew method",
      difficulty: "Difficulty",
      imageUrl: "Image URL (optional)",
      ingredientsEn: "Ingredients (English) — one per line",
      ingredientsAr: "Ingredients (Arabic) — one per line",
      stepsEn: "Steps (English) — one per line",
      stepsAr: "Steps (Arabic) — one per line",
      submit: "Publish recipe",
      success: "Recipe published",
      error: "Could not publish recipe",
      paramsTitle: "Brew Parameters",
      paramsLead: "Defaults are auto-filled from your selected method. Just adjust the numbers.",
      dose: "Coffee dose (g)",
      yield_: "Yield (g / ml)",
      ratio: "Ratio (1:x)",
      temp: "Water temperature (°C)",
      time: "Total time (mm:ss)",
      grind: "Grind size",
      bloomG: "Bloom water (g)",
      bloomS: "Bloom time (s)",
      pressure: "Pressure (bar)",
      notes: "Notes (optional)",
    },
    assistant: {
      title: "AI Barista",
      lead: "Ask anything about coffee — beans, brewing, ratios, recipes.",
      placeholder: "What ratio should I use for a fruity Ethiopian on V60?",
      send: "Send",
      thinking: "Thinking…",
      welcome: "Hi! I'm your AI barista. Ask me about beans, brewing, or recipes.",
    },
    auth: {
      title: "Sign in to Abdullah Coffee Science",
      lead: "Continue with Google to save and share your recipes.",
      google: "Continue with Google",
      required: "Please sign in to continue.",
      signingIn: "Signing you in…",
    },
    difficulty: { easy: "Easy", medium: "Medium", hard: "Hard" },
    common: { back: "Back", loading: "Loading…", close: "Close", save: "Save", cancel: "Cancel", edit: "Edit", delete: "Delete", confirmDelete: "Are you sure you want to delete?" },
    testimonials: {
      eyebrow: "Words From Our Community",
      title: "What students & peers say",
      lead: "Real feedback from baristas, café owners, and coffee enthusiasts who trained with Abdullah.",
      empty: "No reviews yet. Be the first to share your experience.",
      writeReview: "Write a review",
      formTitle: "Share your experience",
      formLead: "Your words help others learn. Your review will appear right away.",
      yourName: "Your name",
      yourRole: "Café / Role (optional)",
      yourReview: "Your review",
      reviewPlaceholder: "Write a few honest lines about your experience…",
      submit: "Publish",
      thanks: "Thanks for your review!",
      tooShort: "Please write at least 10 characters",
    },
    admin: {
      title: "Manage Testimonials",
      add: "Add Testimonial",
      name: "Name",
      cafe: "Café / Role (optional)",
      photo: "Photo URL (optional)",
      text: "Testimonial Text",
      rating: "Rating",
      lang: "Language",
      order: "Display Order",
      langAr: "Arabic",
      langEn: "English",
      onlyAdmin: "This page is restricted to the site owner.",
    },
  },
  ar: {
    appName: "عبدالله | علم القهوة",
    tagline: "من البذرة إلى الرشفة",
    nav: {
      home: "الرئيسية",
      journey: "الرحلة",
      academy: "الأكاديمي",
      encyclopedia: "الموسوعة",
      countries: "البلدان",
      calculator: "الريشيو",
      recipes: "الوصفات",
      addRecipe: "أضف وصفة",
      assistant: "الباريستا الذكي",
      about: "عن عبدالله",
      blog: "المدوّنة",
      login: "تسجيل الدخول",
      logout: "خروج",
      mine: "وصفاتي",
    },
    home: {
      heroTitle: "علم القهوة الكامل.",
      heroLead: "تتبّع كل حبة بن من مزرعة المرتفعات إلى فنجانك. وصفات، نِسَب، وباريستا ذكي — لمن يهتم بالتفاصيل.",
      ctaExplore: "ابدأ الرحلة",
      ctaRecipes: "تصفح الوصفات",
      sectionJourney: "الرحلة",
      sectionJourneyLead: "خمس مراحل تحوّل الكرزة إلى حرفة.",
      sectionOrigins: "ثمانية أصول",
      sectionOriginsLead: "كل دولة تكتب نكهتها الخاصة في الفنجان.",
      sectionTools: "أدوات المحضّر",
      sectionToolsLead: "احسب، حضّر، شارك.",
      missionTag: "هدفنا",
      missionTitle: "أول مرجع ومجتمع عربي متخصص في علم القهوة.",
      missionLead: "منصة تعليمية بُنيت لنشر الوعي، وتمكين الباريستات، وتزويد كل مُحبّ للقهوة بمعرفة موثوقة مبنية على المعايير العالمية — لترتقي الحرفة التي نحبّها بالطريقة التي تستحقها.",
      missionPillar1Title: "مرجع",
      missionPillar1Body: "موسوعة ثنائية اللغة مبنية على معايير SCA — من الزراعة حتى الاستخلاص.",
      missionPillar2Title: "توعية",
      missionPillar2Body: "محتوى واضح ومسارات تعليمية تحوّل الشغوف إلى محضّر واعٍ.",
      missionPillar3Title: "مجتمع",
      missionPillar3Body: "فضاء يلتقي فيه الباريستا وأصحاب الكافيهات ومحضّرو البيت لمشاركة الوصفات ونمو الحرفة معاً.",
    },
    journey: {
      title: "رحلة القهوة",
      lead: "من بذرة صغيرة في تربة بركانية إلى فنجانك — خمس مراحل تصنع كل نكهة.",
      stages: [
        {
          tag: "01 · الزراعة",
          title: "الزراعة",
          body: "تزدهر الأرابيكا بين 1000 و 2200 متر، في الأحزمة الاستوائية ذات الحرارة المستقرة، والتربة البركانية الغنية، والظل. الشجرة الواحدة تحتاج 3-4 سنوات لإنتاج أول حبوب."
        },
        {
          tag: "02 · الحصاد",
          title: "الحصاد",
          body: "تنضج الكرز من الأخضر إلى الأحمر العميق على مدى أشهر. القطف اليدوي الانتقائي يضمن جمع الناضج فقط — مفتاح القهوة المختصة."
        },
        {
          tag: "03 · المعالجة",
          title: "المعالجة",
          body: "طبيعية، مغسولة، عسلية، أو لاهوائية — طريقة معالجة الكرزة تحدد وضوح الفنجان وحلاوته وتعقيده. كل طريقة تروي قصة."
        },
        {
          tag: "04 · التحميص",
          title: "التحميص",
          body: "الحرارة تحوّل الحبوب الخضراء عبر تفاعلات ميلارد والكرملة. من الفاتح إلى الغامق، المحمّص يقرر أي النكهات يبرزها."
        },
        {
          tag: "05 · التحضير",
          title: "التحضير",
          body: "طحن، نسبة، ماء، وقت، حركة. خمسة متغيرات إذا توازنت، أخرجت روح الحبة إلى فنجانك."
        },
      ],
    },
    countries: {
      title: "أصول القهوة",
      lead: "مناشئ زراعية أسطورية على امتداد حزام القهوة — كل منشأ بيئته وتقاليده.",
      varieties: "الأصناف",
      altitude: "الارتفاع",
      harvest: "موسم الحصاد",
      process: "المعالجة",
      flavor: "ملف النكهة",
    },
    calculator: {
      title: "حاسبة الريشيو",
      lead: "اختر طريقة التحضير وسنضبط لك الفنجان المثالي.",
      method: "طريقة التحضير",
      coffee: "البن (جرام)",
      water: "الماء (جرام)",
      ratio: "النسبة (1:n)",
      calculate: "احسب",
      result: "الوصفة",
      grind: "الطحن",
      temp: "الحرارة",
      time: "وقت التحضير",
      steps: "الخطوات",
      defaultRatio: "النسبة الافتراضية",
    },
    recipes: {
      title: "الوصفات",
      lead: "كلاسيكيات منتقاة وأفضل ما لدى مجتمعنا.",
      curated: "منتقاة",
      community: "المجتمع",
      empty: "لا توجد وصفات بعد — كن الأول وشارك وصفتك.",
      add: "أضف وصفة",
      method: "الطريقة",
      ingredients: "المكونات",
      steps: "الخطوات",
      difficulty: "الصعوبة",
      by: "بواسطة",
      delete: "حذف",
    },
    addRecipe: {
      title: "شارك وصفتك",
      lead: "ساعد المجتمع على تحضير قهوة أفضل.",
      titleEn: "العنوان (إنجليزي)",
      titleAr: "العنوان (عربي)",
      method: "طريقة التحضير",
      difficulty: "الصعوبة",
      imageUrl: "رابط صورة (اختياري)",
      ingredientsEn: "المكونات (إنجليزي) — كل مكوّن في سطر",
      ingredientsAr: "المكونات (عربي) — كل مكوّن في سطر",
      stepsEn: "الخطوات (إنجليزي) — كل خطوة في سطر",
      stepsAr: "الخطوات (عربي) — كل خطوة في سطر",
      submit: "انشر الوصفة",
      success: "تم نشر الوصفة",
      error: "تعذّر نشر الوصفة",
      paramsTitle: "متغيّرات التحضير",
      paramsLead: "القيم الافتراضية معبّأة تلقائياً حسب طريقة التحضير. عدّل الأرقام فقط.",
      dose: "كمية البن (جرام)",
      yield_: "الناتج (جرام / مل)",
      ratio: "النسبة (1:x)",
      temp: "حرارة الماء (°م)",
      time: "الوقت الكلّي (د:ث)",
      grind: "حجم الطحن",
      bloomG: "ماء التبليل Bloom (جرام)",
      bloomS: "وقت التبليل Bloom (ثانية)",
      pressure: "الضغط (بار)",
      notes: "ملاحظات (اختياري)",
    },
    assistant: {
      title: "الباريستا الذكي",
      lead: "اسألني عن أي شيء يخص القهوة — الحبوب، التحضير، النِّسَب، الوصفات.",
      placeholder: "ما النسبة المناسبة لقهوة إثيوبية فاكهية في V60؟",
      send: "إرسال",
      thinking: "أفكّر…",
      welcome: "أهلاً! أنا الباريستا الذكي. اسألني عن الحبوب أو التحضير أو الوصفات.",
    },
    auth: {
      title: "سجّل الدخول لـ عبدالله | علم القهوة",
      lead: "تابع باستخدام Google لحفظ ومشاركة وصفاتك.",
      google: "متابعة باستخدام Google",
      required: "الرجاء تسجيل الدخول للمتابعة.",
      signingIn: "جاري تسجيل الدخول…",
    },
    difficulty: { easy: "سهلة", medium: "متوسطة", hard: "صعبة" },
    common: { back: "رجوع", loading: "تحميل…", close: "إغلاق", save: "حفظ", cancel: "إلغاء", edit: "تعديل", delete: "حذف", confirmDelete: "هل أنت متأكد من الحذف؟" },
    testimonials: {
      eyebrow: "آراء المجتمع",
      title: "ماذا يقول الطلاب والزملاء؟",
      lead: "آراء حقيقية من باريستات وأصحاب كافيهات ومحبّي القهوة الذين تدرّبوا مع عبدالله.",
      empty: "لا توجد آراء بعد — كن أول من يشارك تجربته.",
      writeReview: "اكتب رأيك",
      formTitle: "شاركنا تجربتك",
      formLead: "كلماتك تساعد غيرك. رأيك ينشر مباشرة.",
      yourName: "اسمك",
      yourRole: "الكافيه / المنصب (اختياري)",
      yourReview: "نص الرأي",
      reviewPlaceholder: "اكتب بصدق عن تجربتك في عدة أسطر…",
      submit: "انشر",
      thanks: "شكراً لمشاركتنا رأيك!",
      tooShort: "اكتب 10 أحرف على الأقل",
    },
    admin: {
      title: "إدارة الآراء",
      add: "إضافة رأي",
      name: "الاسم",
      cafe: "الكافيه / المنصب (اختياري)",
      photo: "رابط الصورة (اختياري)",
      text: "نص الرأي",
      rating: "التقييم",
      lang: "اللغة",
      order: "ترتيب العرض",
      langAr: "عربي",
      langEn: "إنجليزي",
      onlyAdmin: "هذه الصفحة مخصصة لصاحب الموقع فقط.",
    },
  },
};

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "ar");

  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
    document.body.classList.toggle("font-arabic", lang === "ar");
    document.body.classList.toggle("font-latin", lang !== "ar");
  }, [lang]);

  const toggle = useCallback(() => setLang((l) => (l === "ar" ? "en" : "ar")), []);
  const t = translations[lang];

  const value = useMemo(
    () => ({ lang, setLang, toggle, t, isRTL: lang === "ar" }),
    [lang, toggle, t]
  );

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
};
